import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {PageContainer} from '@amzn/stencil-react-components/page';
import {Col, Row} from "@amzn/stencil-react-components/layout";
import {Button, ButtonVariant} from "@amzn/stencil-react-components/button";
import {IconCheck} from '@amzn/stencil-react-components/icons';
import {
  BindleSelector,
  DefineEventName,
  EventRuleComponent,
  SolutionComponent,
  TriggerSelector
} from "src/components/authoringComponents/index";
import {useAppDispatch, useAppSelector} from "src/state/hooks";
import {
  EventModelState,
  resetEventModelState,
  updateEventModel,
  updatePromoteToProdStatus
} from "src/state/slice/EventModelSlice";
import {
  getEventModel,
  getTriggersSolutionTypes,
  promoteDerivedSegmentationAttributes,
  upsertEventModel
} from "src/service/service";
import {APICallStatus, EventModelStatus, Stage} from "src/enum/enums";
import {MoreOptionsButtonWithPopover} from "./MoreOptionsButtonWithPopover";
import {PromoteToProdButtonWithModal} from "./PromoteToProdButtonWithModal";
import {Spinner} from "@amzn/stencil-react-components/spinner";
import {TestExecuteEventsButtonWithFlyout} from "src/components/testingComponents/index";
import {MessageBanner, MessageBannerType} from "@amzn/stencil-react-components/message-banner";
import {Text} from "@amzn/stencil-react-components/text";
import "src/components/commonStyles.scss";
import {
  getStage,
  isValidateSolutionDetailsInput,
  isValidEventRule, isValidJson,
  mandatorySolutionAttributes
} from "src/util/commonUtil";
import {
  getTriggerAndSolutionTypeAccessErrorMessage,
  PROMOTE_TO_BETA_ERROR_MESSAGE, PROMOTE_TO_PROD_ERROR_MESSAGE, SAVE_ERROR_MESSAGE,
  TRIGGER_SOLUTION_TYPE_ERROR_MESSAGE
} from "src/constant/errorConstants";
import {upsertDerivedSegmentationAttributes} from "src/util/fileUtil";
import {ActionType, AUTO_DISMISS_TIMEOUT, DISMISS, SolutionTypes} from "src/constant/constants";
import {resetFileMetadataState} from "src/state/slice/ContactAlertFileMetadataSlice";
import {EventTimestampSelector} from "src/components/authoringComponents/EventTimestampSelector";

const STRINGS_DISPLAYED = {
  error_fetching_event_model_message: "Error fetching event model. Please try again.",
  save_button: "Save",
  promote_to_beta_button: "Promote to beta",
  promote_to_beta_successful: "Promote to Beta is sucessful!",
  save_successful: "Successfully saved the event!",
  promote_to_prod_successful: "Promote to Prod is sucessful!"
}

/**
 * This component renders the page to let users manage their existing event models.
 */
export const ManageEventModelPage= () => {
  const { eventModelName } = useParams<{ eventModelName: string }>();

  // global event model state
  const dispatch = useAppDispatch();
  const eventModelState = useAppSelector(({eventModel}) => eventModel);
  const contactAlertFileMetadataState = useAppSelector(({contactAlertFileMetadataComponent}) => contactAlertFileMetadataComponent);

  const [triggersAndSolutionTypes, setTriggersAndSolutionTypes] = useState<string[]>([]);
  const [getTriggerSolutionTypesStatus, setGetTriggerSolutionTypesStatus] = useState<APICallStatus>(APICallStatus.IN_PROGRESS);

  // local states
  const [liveEventModelInBeta, setLiveEventModelInBeta] = useState<EventModelState>();
  const [liveEventModelInProd, setLiveEventModelInProd] = useState<EventModelState>();

  const [getEventModelStatus, setGetEventModelStatus] = useState<APICallStatus>(APICallStatus.SUCCESSFUL);
  const [saveEventModelStatus, setSaveEventModelStatus] = useState<APICallStatus>(APICallStatus.NOT_STARTED);
  const [promoteToBetaStatus, setPromoteToBetaStatus] = useState<APICallStatus>(APICallStatus.NOT_STARTED);
  const [requiredSolutionAttributes, setRequiredSolutionAttributes] = useState<string[]>([]);
  const [promoteToBetaErrorMessage, setPromoteToBetaErrorMessage] = useState("");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [isValidRule, setIsValidRule] = useState<boolean>(true)


  useEffect(() => {
    setGetEventModelStatus(APICallStatus.IN_PROGRESS);
    // fetch draft event model from beta to render on the UI
    getEventModel(eventModelName, EventModelStatus.DRAFT, Stage.beta)
      .then((eventModelDefinition) => {
        dispatch(updateEventModel(eventModelDefinition));
        setGetEventModelStatus(APICallStatus.SUCCESSFUL);
      })
      .catch((_) => {
        setGetEventModelStatus(APICallStatus.FAILED);
      });

    // fetch triggers and solutionTypes
    getTriggersSolutionTypes(getStage() as Stage)
      .then(triggersSolutionTypes => {
        setTriggersAndSolutionTypes(triggersSolutionTypes);
        setGetTriggerSolutionTypesStatus(APICallStatus.SUCCESSFUL)
      }).catch( (_) => {
        setGetTriggerSolutionTypesStatus(APICallStatus.FAILED);
    })

    // fetch live event model from beta to enable prod promotion
    getEventModel(eventModelName, EventModelStatus.LIVE, Stage.beta)
      .then((eventModelDefinition) => {
        setLiveEventModelInBeta(eventModelDefinition);
      });

    // fetch live event model from prod
    getEventModel(eventModelName, EventModelStatus.LIVE, Stage.prod)
      .then((eventModelDefinition) => {
        setLiveEventModelInProd(eventModelDefinition);
      });

    //clean up global state on unmount
    return () => {
      dispatch(resetEventModelState());
      dispatch(resetFileMetadataState());
    }
  }, [eventModelName]);

  useEffect(() => {
    setRequiredSolutionAttributes(mandatorySolutionAttributes(eventModelState?.eventSolution?.type));
  }, [eventModelState?.eventSolution?.type]);

  //to enable Promote to Prod button once the Promote to beta is completed
  useEffect(() => {
    // fetch live event model from beta to enable prod promotion
    getEventModel(eventModelName, EventModelStatus.LIVE, Stage.beta)
        .then((eventModelDefinition) => {
          setLiveEventModelInBeta(eventModelDefinition);
        });
  }, [promoteToBetaStatus]);

  const onClickSaveButton = async () => {
    setSaveEventModelStatus(APICallStatus.IN_PROGRESS);
    if (contactAlertFileMetadataState?.dataToBePublished.length > 0 && contactAlertFileMetadataState?.entityHeader) {
      await upsertDerivedSegmentationAttributes(eventModelState, contactAlertFileMetadataState.dataToBePublished,
          contactAlertFileMetadataState.entityHeader, contactAlertFileMetadataState.existingBulkUploadFileAttributes);
    }
    upsertEventModel(eventModelState, EventModelStatus.DRAFT, Stage.beta)
      .then((_) => {
        setSaveEventModelStatus(APICallStatus.SUCCESSFUL);
      })
      .catch((error) => {
        setSaveEventModelStatus(APICallStatus.FAILED);
        setSaveErrorMessage(error?.response?.data?.message);
      });
  };

  const onClickPromoteToBeta = async () => {
    setPromoteToBetaStatus(APICallStatus.IN_PROGRESS);
    if (eventModelState?.eventSolution?.type === SolutionTypes.CONTACT_ALERT &&
        getStage() as Stage === Stage.prod &&
        contactAlertFileMetadataState?.newBulkUploadFileAttributes.length > 0) {
      await promoteDerivedSegmentationAttributes(contactAlertFileMetadataState.newBulkUploadFileAttributes, Stage.beta);
    }
    upsertEventModel(eventModelState, EventModelStatus.LIVE, Stage.beta)
      .then((_) => {
        setPromoteToBetaStatus(APICallStatus.SUCCESSFUL);
      })
      .catch((error) => {
        setPromoteToBetaErrorMessage(error?.response?.data?.message);
        setPromoteToBetaStatus(APICallStatus.FAILED);
      });

  };

  useEffect(() => {
    if(isValidJson(eventModelState?.eventRule)){
      const eventRuleToValidate = eventModelState?.eventRule ? JSON.stringify(JSON.parse(eventModelState?.eventRule)) : undefined;
      setIsValidRule(isValidEventRule((eventRuleToValidate)));
    }
  }, [eventModelState?.eventRule]);

  const createMessageBanner = (type, message, onDismissed, errorMessage = null) => (
      <MessageBanner
          dismissButtonAltText={DISMISS}
          isDismissible
          autoDismissAfter={AUTO_DISMISS_TIMEOUT}
          onDismissed={onDismissed}
          type={type}
      >
        {message}
        {errorMessage && <br />}
        {errorMessage}
      </MessageBanner>
  );

  const showMessageBanner = () => {
    const commonProps = {
      dismissButtonAltText: DISMISS,
      isDismissible: true,
      autoDismissAfter: AUTO_DISMISS_TIMEOUT,
    };

    switch (true) {
      case promoteToBetaStatus === APICallStatus.SUCCESSFUL:
        return createMessageBanner(
            MessageBannerType.Success,
            STRINGS_DISPLAYED.promote_to_beta_successful,
            () => setPromoteToBetaStatus(APICallStatus.COMPLETED),
            null
        );
      case promoteToBetaStatus === APICallStatus.FAILED:
        return createMessageBanner(
            MessageBannerType.Error,
            PROMOTE_TO_BETA_ERROR_MESSAGE,
            () => setPromoteToBetaStatus(APICallStatus.COMPLETED),
            promoteToBetaErrorMessage
        );
      case saveEventModelStatus === APICallStatus.SUCCESSFUL:
        return createMessageBanner(
            MessageBannerType.Success,
            STRINGS_DISPLAYED.save_successful,
            () => setSaveEventModelStatus(APICallStatus.COMPLETED),
            null
        );
      case saveEventModelStatus === APICallStatus.FAILED:
        return createMessageBanner(
            MessageBannerType.Error,
            SAVE_ERROR_MESSAGE,
            () => setSaveEventModelStatus(APICallStatus.COMPLETED),
            saveErrorMessage
        );
      case eventModelState?.promoteToProdStatus == APICallStatus.SUCCESSFUL.toString():
        return createMessageBanner(
            MessageBannerType.Success,
            STRINGS_DISPLAYED.promote_to_prod_successful,
            () => dispatch(updatePromoteToProdStatus(APICallStatus.COMPLETED.toString())),
            null
        );
      case eventModelState?.promoteToProdStatus == APICallStatus.FAILED.toString():
        return createMessageBanner(
            MessageBannerType.Error,
            PROMOTE_TO_PROD_ERROR_MESSAGE,
            () => dispatch(updatePromoteToProdStatus(APICallStatus.COMPLETED.toString())),
            eventModelState?.promoteToProdError
        );
      default:
        return null;
    }
  };

  return <PageContainer backgroundColor={"white"}>
    {getEventModelStatus === APICallStatus.IN_PROGRESS
      ? <Spinner />
      : <Col gridGap={"24px"}>

        {getEventModelStatus === APICallStatus.FAILED && <MessageBanner type={MessageBannerType.Error}>
          {STRINGS_DISPLAYED.error_fetching_event_model_message}
        </MessageBanner>}

        {getEventModelStatus === APICallStatus.SUCCESSFUL && getTriggerSolutionTypesStatus === APICallStatus.FAILED &&
            <MessageBanner type={MessageBannerType.Error} autoDismissAfter={5000}>
              { TRIGGER_SOLUTION_TYPE_ERROR_MESSAGE }
            </MessageBanner>}

        {getEventModelStatus === APICallStatus.SUCCESSFUL && getTriggerSolutionTypesStatus === APICallStatus.SUCCESSFUL &&
          getTriggerAndSolutionTypeAccessErrorMessage(triggersAndSolutionTypes, eventModelState.clientUseCase, eventModelState.eventSolution?.type) &&
          <MessageBanner type={MessageBannerType.Warning}>
              {getTriggerAndSolutionTypeAccessErrorMessage(triggersAndSolutionTypes, eventModelState.clientUseCase, eventModelState.eventSolution?.type)}
          </MessageBanner>}
        
        <DefineEventName disabled={true} isEventModelExist={false} isValid={true} />
        <BindleSelector disabled={false} />
        <TriggerSelector disabled={true} triggersAndSolutionTypes={triggersAndSolutionTypes}/>
        <EventTimestampSelector />
        <EventRuleComponent isValidRule={isValidRule}/>
        <SolutionComponent triggersAndSolutionTypes={triggersAndSolutionTypes} actionType={ActionType.EDIT}/>

        <Row gridGap={"24px"}>
          <Button
            variant={ButtonVariant.Primary}
            className={"width_fit_content"}
            onClick={onClickSaveButton}
            disabled={!(eventModelState.eventName && saveEventModelStatus !== APICallStatus.IN_PROGRESS) ||
                !isValidateSolutionDetailsInput(eventModelState, requiredSolutionAttributes) || !(contactAlertFileMetadataState?.isValidFileFormatForBulkUpload)
                || !isValidRule}
          >
            <Text fontSize={"16px"}>{STRINGS_DISPLAYED.save_button}</Text>
            {(saveEventModelStatus === APICallStatus.IN_PROGRESS) && <Spinner />}
          </Button>

          <Button
            variant={ButtonVariant.Secondary}
            icon={<IconCheck aria-hidden={true} />}
            className={"width_fit_content"}
            onClick={onClickPromoteToBeta}
            disabled={!(eventModelState.eventName && promoteToBetaStatus !== APICallStatus.IN_PROGRESS) ||
                !isValidateSolutionDetailsInput(eventModelState, requiredSolutionAttributes) || !isValidRule}
          >
            <Text fontSize={"16px"}>{STRINGS_DISPLAYED.promote_to_beta_button}</Text>
            {(promoteToBetaStatus === APICallStatus.IN_PROGRESS) && <Spinner />}
          </Button>

          <TestExecuteEventsButtonWithFlyout 
            isLiveEventModelPresentInBeta={Boolean(liveEventModelInBeta)}
          />

          <PromoteToProdButtonWithModal
            eventSolutionType={eventModelState?.eventSolution?.type}
            eventModelName={eventModelState.eventName}
            solutionType={eventModelState.eventSolution?.type}
            isLiveEventModelPresentInBeta={Boolean(liveEventModelInBeta)}
            isValidSolutionDetailsInput={isValidateSolutionDetailsInput(eventModelState, requiredSolutionAttributes)}
            isValidRule={isValidRule}
          />

          <MoreOptionsButtonWithPopover
            isLiveEventModelPresentInBeta={Boolean(liveEventModelInBeta)}
            isLiveEventModelPresentInProd={Boolean(liveEventModelInProd)}
          />
        </Row>
          {showMessageBanner()}
      </Col>
    }
  </PageContainer>
}
